/* 
 *
 * glossary.js 
 *
 */

import {
    createAsyncThunk,
    createSlice
} from "@reduxjs/toolkit";
import glossaryAPI from "../../api/glossaryAPI";
import thunk from "../../helpers/thunk";

/* thunks */

const fetchGlossariesByTitle = thunk(
    "glossary/fetchGlossariesByTitle",
    glossaryAPI.fetchByTitle,
    o => ({
        data: o.r.data,
        titleID: o.d.titleID
    })
);

const fetchGlossariesByProject = thunk(
    "glossary/fetchGlossariesByProject",
    glossaryAPI.fetchByProject,
    o => ({
        data: o.r.data,
        projectID: o.d.projectID
    })
);


const fetchGlossariesByTask = thunk(
    "glossary/fetchGlossariesByTask",
    glossaryAPI.fetchByTask,
    o => ({
        data: o.r.data,
        taskID: o.d.taskID
    })
);

const fetchGlossaries = thunk(
    "glossary/fetchGlossaries",
    glossaryAPI.fetchAll,
    o => o.r.data
);

const fetchGlossary = thunk(
    "glossary/fetchGlossary",
    glossaryAPI.fetchByID,
    o => o.r.data
);

const assignGlossaryToTitle = thunk(
    "glossary/assignGlossaryToTitle",
    glossaryAPI.assignToTitle,
    o => ({
        data: o.r.data,
        titleID: o.d.titleID
    })
);

const unassignGlossaries = thunk(
    "glossary/unassignGlossaries",
    glossaryAPI.unassign,
    o => ({
        glossaryIDs: o.d.glossaryIDs,
        titleID: o.d.titleID,
        projectID: o.d.projectID
    })
);

const addTerm = thunk(
    "glossary/addTerm",
    glossaryAPI.addTerm,
    o => ({
        termID: o.r.data.termID,
        glossaryID: o.d.glossaryID,
        source: o.d.source,
        target: o.d.target,
        note: o.d.note
    })
);

const removeTerms = thunk(
    "glossary/removeTerms",
    glossaryAPI.removeTerms,
    o => ({
        termIDs: o.d.termIDs,
        glossaryID: o.d.glossaryID
    })
);

const assignGlossaryToProject = thunk(
    "glossary/assignGlossaryToProject",
    glossaryAPI.assignToProject,
    o => ({
        data: o.r.data,
        projectID: o.d.projectID
    })
);

const createGlossary = thunk(
    "glossary/createGlossary",
    glossaryAPI.create,
    o => o.r.data
);

const saveGlossaryModifications = createAsyncThunk(
    "glossary/saveGlossaryModifications",
    async (data, {
        rejectWithValue,
        getState
    }) => {
        const id = [];
        const source = [];
        const target = [];
        const glossary = getState().glossary.glossaries[data.glossaryID];

        Object.keys(glossary.terms).forEach(ID => {
            const term = glossary.terms[ID];
            const termID = term.id;

            if (term.modified) {
                id.push(termID);
                source.push(term.source);
                target.push(term.targets);
            }
        });

        return (
            await glossaryAPI.saveModifications({
                id,
                source,
                target
            }).then(r => {
                return {
                    glossaryID: data.glossaryID
                }
            }).catch(e => rejectWithValue())
        );
    }
);


const setGlossaryName = thunk(
    "glossary/setGlossaryName",
    glossaryAPI.setName,
    o => ({
        ...o.d
    })
);

const deleteGlossary = thunk(
    "glossary/deleteGlossary",
    glossaryAPI.delete,
    o => o.d
);

const setGlossaryContractorWrite = thunk(
    "glossary/setGlossaryContractorWrite",
    glossaryAPI.setContractorWrite,
    o => o.d
);

/* slice */
const slice = createSlice({
    name: "glossary",
    initialState: {
        glossaries: {

        }
    },
    reducers: {
        termSourceModified: (state, action) => {
            const glossaryID = action.payload.glossaryID;
            const termID = action.payload.termID;
            const sourceText = action.payload.sourceText;
            const term = state.glossaries[glossaryID].terms[termID];
            term.source = sourceText;
            term.modified = true;
        },
        termTargetModified: (state, action) => {
            const glossaryID = action.payload.glossaryID;
            const termID = action.payload.termID;
            const targetText = action.payload.targetText;
            const targetLang = action.payload.targetLang;

            const term = state.glossaries[glossaryID].terms[termID];
            term.modified = true;
            const target = term.targets.find(target => target.lang == targetLang);
            if (target) {
                target.text = targetText;
            } else {
                state.glossaries[glossaryID].terms[termID].targets.push({
                    text: targetText,
                    lang: targetLang
                });
            }
        }
    },
    extraReducers: {
        [fetchGlossariesByTitle.fulfilled]: (state, action) => {
            const glossaries = action.payload.data;

            glossaries.forEach(glossary => {
                state.glossaries[glossary.ID] = {
                    name: glossary.name,
                    desc: glossary.desc,
                    sourceLang: glossary.sourceLang,
                    targetLang: glossary.targetLang
                }
            });
        },
        [fetchGlossariesByProject.fulfilled]: (state, action) => {
            const glossaries = action.payload.data;
            glossaries.forEach(glossary => {
                state.glossaries[glossary.ID] = {
                    name: glossary.name,
                    desc: glossary.desc,
                    sourceLang: glossary.sourceLang,
                    targetLang: glossary.targetLang
                }
            });
        },
        [fetchGlossariesByTask.fulfilled]: (state, action) => {
            const glossaries = action.payload.data;
            glossaries.forEach(glossary => {
                state.glossaries[glossary.ID] = {
                    name: glossary.name,
                    desc: glossary.desc,
                    sourceLang: glossary.sourceLang,
                    targetLang: glossary.targetLang
                }
            });
        },
        [fetchGlossaries.fulfilled]: (state, action) => {
            const glossaries = action.payload;

            glossaries.forEach(glossary => {
                state.glossaries[glossary.ID] = {
                    name: glossary.name,
                    desc: glossary.desc,
                    sourceLang: glossary.sourceLang,
                    targetLang: glossary.targetLang
                }
            });
        },
        [assignGlossaryToTitle.fulfilled]: (state, action) => {
            const glossary = action.payload.data;

            state.glossaries[glossary.ID] = {
                name: glossary.name,
                desc: glossary.desc,
                sourceLang: glossary.sourceLang,
                targetLang: glossary.targetLang
            }
        },
        [assignGlossaryToProject.fulfilled]: (state, action) => {
            const glossary = action.payload.data;

            state.glossaries[glossary.ID] = {
                name: glossary.name,
                desc: glossary.desc,
                sourceLang: glossary.sourceLang,
                targetLang: glossary.targetLang
            }
        },
        [createGlossary.fulfilled]: (state, action) => {
            const glossary = action.payload;

            state.glossaries[glossary.ID] = {
                name: glossary.name,
                desc: glossary.desc,
                sourceLang: glossary.sourceLang,
                targetLang: glossary.targetLang
            }
        },
        [fetchGlossary.fulfilled]: (state, action) => {
            const glossary = action.payload;

            state.glossaries[glossary.ID] = {
                name: glossary.name,
                note: glossary.note,
                sourceLang: glossary.sourceLang,
                terms: glossary.terms,
                size: glossary.size,
                created: glossary.created
            }
        },
        [addTerm.fulfilled]: (state, action) => {
            const glossaryID = action.payload.glossaryID;
            const termID = action.payload.termID;
            const source = action.payload.source;
            const target = action.payload.target || {lang:"", text:""};
            const note = action.payload.note;

            state.glossaries[glossaryID].terms.push({
                id: termID,
                source,
                note: note,
                targets: [target]
            });
        },
        [removeTerms.fulfilled]: (state, action) => {
            const termIDs = action.payload.termIDs;
            const glossaryID = action.payload.glossaryID;

            state.glossaries[glossaryID].terms.forEach((term, index) => {
                if (termIDs.includes(term.id)) delete state.glossaries[glossaryID].terms[index];
            });
        },
        [saveGlossaryModifications.fulfilled]: (state, action) => {
            const glossaryID = action.payload.glossaryID;

            state.glossaries[glossaryID].terms.forEach(term => {
                delete term.modified;
            });
        },
        [setGlossaryName.fulfilled] : (state, action) => {
            const glossaryID = action.payload.glossaryID;
            const glossaryName = action.payload.glossaryName;

            state.glossaries[glossaryID].name = glossaryName;
        },
        [deleteGlossary.fulfilled] : (state,action) => {
            const glossaryID = action.payload.glossaryID;

            delete state.glossaries[glossaryID];
        }
    }
});

export {
    fetchGlossariesByTitle,
    fetchGlossaries,
    fetchGlossariesByTask,
    assignGlossaryToTitle,
    assignGlossaryToProject,
    fetchGlossariesByProject,
    createGlossary,
    fetchGlossary,
    addTerm,
    removeTerms,
    setGlossaryContractorWrite,
    saveGlossaryModifications,
    unassignGlossaries,
    setGlossaryName,
    deleteGlossary
};
export const {
    termSourceModified,
    termTargetModified
} = slice.actions;
export default slice.reducer;